import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import type { Redirect } from 'next';
import getConfig from 'next/config';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig();

const LOG = LoggerFactory.create('customDomain');

function redirect(domain: string, path: string): Redirect {
  return {
    destination: `${domain}${publicRuntimeConfig.BASE_PATH}${path}`,
    basePath: false, // needed for external redirects
    permanent: false
  };
}

/**
 * Returns a redirect when the host does not correctly match the domain configured for the event
 * @param host the host that was used to access the site
 * @param url the url requested by the user, not including the base path (i.e. /hub)
 * @param eventInfo the event data fetched from event-search
 * @param appFeatures collection of feature flags configuring the behaviour of the app
 * @returns {Redirect} when the user's browser should be redirected to the configured domain, otherwise undefined
 */
export function getDomainRedirect(
  host: string,
  url: string,
  customDomain: string | null,
  appFeatures: Record<string, boolean>,
  skipCustomDomain?: boolean
): Redirect | void {
  const VALID_HOSTS = [publicRuntimeConfig.PUBLIC_URL, serverRuntimeConfig.CLOUDFRONT_URL].map(
    hostUrl => new URL(hostUrl).host
  );

  // Custom domains can be configured but not enabled by the experiment
  if (!skipCustomDomain && appFeatures.customDomainRedirect) {
    if (customDomain && host !== customDomain) {
      LOG.debug(`Custom domain is: ${customDomain}, but arrived through ${host}`);
      return redirect(`https://${customDomain}`, url);
    }
  } else if (!VALID_HOSTS.includes(host)) {
    // This ensures users do not use custom domains when the feature
    // is not enabled. Redirect to the standard (gateway) Attendee Hub URL
    // when the site was accessed from elsewhere.
    return redirect(publicRuntimeConfig.PUBLIC_URL, url);
  }
}
